/**
 * AD-009 layer 1: the shell's menu system as plain data plus pure transitions.
 * Nothing here knows about pixels, canvases or Solid — `render/` turns a
 * `ShellState` into draw operations, and only the route components hold it in
 * a signal. Keeping it separate is what lets a second render backend (hi-res
 * redrawn art, vector fonts) replace `render/` without touching navigation.
 */

/**
 * What activating an item does. Screens name routes as strings so this module
 * stays router-agnostic, and `command` is the same idea one level further out:
 * a name the surface that owns the screen knows how to act on (re-roll the
 * front, toggle a realism setting, enlist a pilot) without this module having
 * to know what any of those mean.
 */
export type MenuAction =
  | { readonly kind: "navigate"; readonly path: string }
  | { readonly kind: "open"; readonly screen: string }
  | { readonly kind: "close" }
  | { readonly kind: "command"; readonly command: string }
  | { readonly kind: "replayIntro" }
  | { readonly kind: "quit" };

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly action: MenuAction;
}

/**
 * `"panel"` is the Main Menu's full-height framed board (ornaments, header
 * banner); `"popup"` is the smaller flat board the original uses for
 * `OTHER OPTIONS` and every confirmation dialog.
 */
export type MenuScreenKind = "panel" | "popup";

export interface MenuScreen {
  readonly id: string;
  readonly kind: MenuScreenKind;
  /** Header text — `MAIN MENU` on the board's banner, `CHOOSE` inside a popup. */
  readonly title: string;
  /** Prose lines above the buttons; only dialogs use them. */
  readonly body: readonly string[];
  readonly items: readonly MenuItem[];
  /** What `Escape` does here. The Main Menu has nothing to back out to. */
  readonly cancel: MenuAction | undefined;
}

export interface MenuTree {
  readonly root: string;
  readonly screens: ReadonlyMap<string, MenuScreen>;
}

export function buildMenuTree(root: string, screens: readonly MenuScreen[]): MenuTree {
  return { root, screens: new Map(screens.map((screen) => [screen.id, screen])) };
}

/**
 * The open screens, innermost last, with each one's own selection index.
 * A stack rather than a single id because the original draws its popups
 * *over* the Main Menu rather than replacing it.
 */
export interface ShellState {
  readonly stack: readonly string[];
  readonly selection: readonly number[];
}

export function initialShellState(tree: MenuTree): ShellState {
  return { stack: [tree.root], selection: [0] };
}

export function activeScreenId(state: ShellState): string {
  return state.stack[state.stack.length - 1] ?? "";
}

export function activeScreen(tree: MenuTree, state: ShellState): MenuScreen | undefined {
  return tree.screens.get(activeScreenId(state));
}

export function activeSelection(state: ShellState): number {
  return state.selection[state.selection.length - 1] ?? 0;
}

function withSelection(state: ShellState, index: number): ShellState {
  const selection = state.selection.slice();
  selection[selection.length - 1] = index;
  return { stack: state.stack, selection };
}

/** Wraps at both ends, matching the original's arrow-key behaviour on a fixed button column. */
export function moveSelection(tree: MenuTree, state: ShellState, delta: number): ShellState {
  const screen = activeScreen(tree, state);
  const count = screen?.items.length ?? 0;
  if (count === 0) return state;
  const next = (((activeSelection(state) + delta) % count) + count) % count;
  return withSelection(state, next);
}

export function setSelection(tree: MenuTree, state: ShellState, index: number): ShellState {
  const screen = activeScreen(tree, state);
  if (!screen || index < 0 || index >= screen.items.length) return state;
  return withSelection(state, index);
}

export function pushScreen(tree: MenuTree, state: ShellState, screenId: string): ShellState {
  if (!tree.screens.has(screenId) || state.stack.includes(screenId)) return state;
  return { stack: [...state.stack, screenId], selection: [...state.selection, 0] };
}

/** Closing the root is a no-op — the original's Main Menu has no parent. */
export function popScreen(state: ShellState): ShellState {
  if (state.stack.length <= 1) return state;
  return { stack: state.stack.slice(0, -1), selection: state.selection.slice(0, -1) };
}

/**
 * What the outside world still has to do after a state transition: open/close
 * are handled internally, everything else escapes as an effect so the route
 * component owns navigation and audio.
 */
export type ShellEffect =
  | { readonly kind: "navigate"; readonly path: string }
  | { readonly kind: "command"; readonly command: string }
  | { readonly kind: "replayIntro" }
  | { readonly kind: "quit" };

export interface ShellTransition {
  readonly state: ShellState;
  readonly effect: ShellEffect | undefined;
}

export function applyAction(tree: MenuTree, state: ShellState, action: MenuAction | undefined): ShellTransition {
  if (!action) return { state, effect: undefined };
  switch (action.kind) {
    case "open":
      return { state: pushScreen(tree, state, action.screen), effect: undefined };
    case "close":
      return { state: popScreen(state), effect: undefined };
    case "navigate":
      return { state, effect: { kind: "navigate", path: action.path } };
    case "command":
      return { state, effect: { kind: "command", command: action.command } };
    case "replayIntro":
      return { state: popScreen(state), effect: { kind: "replayIntro" } };
    case "quit":
      return { state, effect: { kind: "quit" } };
  }
}

export function activateSelection(tree: MenuTree, state: ShellState): ShellTransition {
  const screen = activeScreen(tree, state);
  return applyAction(tree, state, screen?.items[activeSelection(state)]?.action);
}

export function cancelScreen(tree: MenuTree, state: ShellState): ShellTransition {
  return applyAction(tree, state, activeScreen(tree, state)?.cancel);
}
